<template>
  <div>
    <h1 class="sr-only">Profile</h1>
    <!-- Main 3 column grid -->
    <div class="bg-white rounded-lg mb-4 flex flex-col overflow-hidden shadow-md" v-if="userInfo.w9_timestamp == null">
      <div class="font-medium p-3 bg-red-600 text-white flex flex-row space-x-2 items-center content-center ">
        <i class="fa-regular fa-triangle-exclamation"></i>
        <h1>Account Action Required</h1>
      </div>
      <div class="p-3">
        In order to receive your next payout, you must complete a new
        <a href="https://www.irs.gov/pub/irs-pdf/fw9.pdf" target="_blank" class="text-blue-600">
          W-9 form</a>
        and email it to
        <a href="mailto:formslsm@gmail.com" class="text-blue-600">formslsm@gmail.com</a>. Once your form is received,
        this message will disappear (within a
        couple of business days).
      </div>
    </div>
    <div class="grid grid-cols-1 gap-4 items-start lg:grid-cols-3 lg:gap-8">
      <!-- Left column -->
      <div class="grid grid-cols-1 gap-4 lg:col-span-2">
        <!-- Welcome panel -->
        <section aria-labelledby="profile-overview-title">
          <div class="rounded-lg bg-white overflow-hidden shadow">
            <h2 class="sr-only" id="profile-overview-title">
              Profile Overview
            </h2>
            <div class="bg-white p-6">
              <div class="sm:flex sm:items-center sm:justify-between">
                <div class="sm:flex sm:space-x-5">
                  <div class="flex-shrink-0 flex justify-center">
                    <AccountIcon :initials="userInitials" :size="20" />
                  </div>
                  <div class="mt-4 text-center sm:mt-0 sm:pt-1 sm:text-left">
                    <p class="text-sm font-medium text-gray-600">
                      Welcome back,
                    </p>
                    <p class="text-xl font-bold text-gray-900 sm:text-2xl">
                      {{ userInfo.name }}
                    </p>
                    <p class="text-sm font-medium text-gray-600">
                      {{ userInfo.role | displayRole }}
                    </p>
                  </div>
                </div>
                <div class="mt-5 flex justify-center sm:mt-0">
                  <router-link
                    class="flex justify-center items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                    :to="{ name: 'SettingsProfile' }">View profile</router-link>
                </div>
              </div>
            </div>
            <div class="border-t border-gray-200 bg-gray-50 grid grid-cols-1 divide-y divide-gray-200">
              <div class="px-6 py-5 text-sm font-medium space-x-1">
                <span class="text-gray-900">{{
                  userInfo.created_at | timeAgo(true)
                }}</span>
                <span class="text-gray-600">with Live Storms Media</span>
              </div>
            </div>
          </div>
        </section>
        <section>

          <div class="rounded-lg bg-white overflow-hidden shadow">
            <div class="flex flex-row items-center justify-between p-4">
              <h1>Recent Activity</h1>
              <span class="px-3 py-1 bg-gray-100 text-gray-500 rounded-full uppercase text-sm">Past Year</span>
            </div>
            <div class="h-40">
              <VueApexCharts type="area" height="100%" ref="overviewChart" :options="chartOptions" :series="series" />
            </div>
          </div>




        </section>
        <!-- Actions panel -->
        <section aria-labelledby="quick-links-title">
          <div
            class="rounded-lg bg-gray-200 overflow-hidden shadow divide-y divide-gray-200 sm:divide-y-0 sm:grid sm:grid-cols-2 sm:gap-px">
            <h2 class="sr-only" id="quick-links-title">Quick links</h2>

            <router-link :to="{ name: 'StringerUpload' }"
              class="block rounded-tl-lg rounded-tr-lg sm:rounded-tr-none relative group bg-white p-6 focus-within:ring-2 focus-within:ring-inset focus-within:ring-blue-500">
              <div>
                <span class="rounded-lg inline-flex p-3 bg-blue-50 text-blue-700 ring-4 ring-white">
                  <div class="h-6 w-6 flex items-center justify-center">
                    <i class="fad fa-upload"></i>
                  </div>
                </span>
              </div>
              <div class="mt-8">
                <h3 class="text-lg font-medium">
                  <a href="#" class="focus:outline-none">
                    <!-- Extend touch target to entire panel -->
                    <span class="absolute inset-0" aria-hidden="true"></span>
                    Upload Video
                  </a>
                </h3>
                <p class="mt-2 text-sm text-gray-500">
                  From b-roll to stock video, this is where the pathway that can
                  deliver your video to our media clients.
                </p>
              </div>
              <span class="pointer-events-none absolute top-6 right-6 text-gray-300 group-hover:text-gray-400"
                aria-hidden="true">
                <svg class="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 24 24">
                  <path
                    d="M20 4h1a1 1 0 00-1-1v1zm-1 12a1 1 0 102 0h-2zM8 3a1 1 0 000 2V3zM3.293 19.293a1 1 0 101.414 1.414l-1.414-1.414zM19 4v12h2V4h-2zm1-1H8v2h12V3zm-.707.293l-16 16 1.414 1.414 16-16-1.414-1.414z" />
                </svg>
              </span>
            </router-link>

            <router-link :to="{ name: 'StringerAnnouncements' }"
              class="block sm:rounded-tr-lg relative group bg-white p-6 focus-within:ring-2 focus-within:ring-inset focus-within:ring-blue-500">
              <div>
                <span class="rounded-lg inline-flex p-3 bg-rose-50 text-rose-700 ring-4 ring-white">
                  <div class="h-6 w-6 flex items-center justify-center">
                    <i class="fad fa-bullhorn text-xl"></i>
                  </div>
                </span>
              </div>
              <div class="mt-8">
                <h3 class="text-lg font-medium">
                  <a href="#" class="focus:outline-none">
                    <!-- Extend touch target to entire panel -->
                    <span class="absolute inset-0" aria-hidden="true"></span>
                    Announcements
                  </a>
                </h3>
                <p class="mt-2 text-sm text-gray-500">
                  Catch-up on all operations ranging from potential setups and
                  possible tips events.
                </p>
              </div>
              <span class="pointer-events-none absolute top-6 right-6 text-gray-300 group-hover:text-gray-400"
                aria-hidden="true">
                <svg class="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 24 24">
                  <path
                    d="M20 4h1a1 1 0 00-1-1v1zm-1 12a1 1 0 102 0h-2zM8 3a1 1 0 000 2V3zM3.293 19.293a1 1 0 101.414 1.414l-1.414-1.414zM19 4v12h2V4h-2zm1-1H8v2h12V3zm-.707.293l-16 16 1.414 1.414 16-16-1.414-1.414z" />
                </svg>
              </span>
            </router-link>

            <router-link :to="{ name: 'StringerSettings' }"
              class="block relative group bg-white p-6 focus-within:ring-2 focus-within:ring-inset focus-within:ring-blue-500">
              <div>
                <span class="rounded-lg inline-flex p-3 bg-gray-50 text-gray-700 ring-4 ring-white">
                  <div class="h-6 w-6 flex items-center justify-center">
                    <i class="fad fa-drone-alt text-xl"></i>
                  </div>
                </span>
              </div>
              <div class="mt-8">
                <h3 class="text-lg font-medium">
                  <a href="#" class="focus:outline-none">
                    <!-- Extend touch target to entire panel -->
                    <span class="absolute inset-0" aria-hidden="true"></span>
                    FAA Documents
                  </a>
                </h3>
                <p class="mt-2 text-sm text-gray-500">
                  Upload required FAA documentation, such as your certificate
                  (front and back), waivers, and your biyearly recurrent test
                  results.
                </p>
              </div>

              <span class="pointer-events-none absolute top-6 right-6 text-gray-300 group-hover:text-gray-400"
                aria-hidden="true">
                <svg class="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 24 24">
                  <path
                    d="M20 4h1a1 1 0 00-1-1v1zm-1 12a1 1 0 102 0h-2zM8 3a1 1 0 000 2V3zM3.293 19.293a1 1 0 101.414 1.414l-1.414-1.414zM19 4v12h2V4h-2zm1-1H8v2h12V3zm-.707.293l-16 16 1.414 1.414 16-16-1.414-1.414z" />
                </svg>
              </span>
            </router-link>

            <router-link :to="{ name: 'StringerVideoSales' }"
              class="block relative group bg-white p-6 focus-within:ring-2 focus-within:ring-inset focus-within:ring-blue-500">
              <div>
                <span class="rounded-lg inline-flex p-3 bg-yellow-50 text-yellow-700 ring-4 ring-white">
                  <!-- Heroicon name: outline/cash -->
                  <svg class="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                    stroke="currentColor" aria-hidden="true">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </span>
              </div>
              <div class="mt-8">
                <h3 class="text-lg font-medium">
                  <a href="#" class="focus:outline-none">
                    <!-- Extend touch target to entire panel -->
                    <span class="absolute inset-0" aria-hidden="true"></span>
                    Sales
                  </a>
                </h3>
                <p class="mt-2 text-sm text-gray-500">
                  Monitor current and past sales to media organizations in
                  addition to expected payout amounts.
                </p>
              </div>
              <span class="pointer-events-none absolute top-6 right-6 text-gray-300 group-hover:text-gray-400"
                aria-hidden="true">
                <svg class="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 24 24">
                  <path
                    d="M20 4h1a1 1 0 00-1-1v1zm-1 12a1 1 0 102 0h-2zM8 3a1 1 0 000 2V3zM3.293 19.293a1 1 0 101.414 1.414l-1.414-1.414zM19 4v12h2V4h-2zm1-1H8v2h12V3zm-.707.293l-16 16 1.414 1.414 16-16-1.414-1.414z" />
                </svg>
              </span>
            </router-link>

            <router-link :to="{ name: 'StringerSupport' }"
              class="block sm:rounded-bl-lg relative group bg-white p-6 focus-within:ring-2 focus-within:ring-inset focus-within:ring-blue-500">
              <div>
                <span class="rounded-lg inline-flex p-3 bg-light-blue-50 text-light-blue-700 ring-4 ring-white">
                  <!-- Heroicon name: outline/users -->
                  <svg class="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                    stroke="currentColor" aria-hidden="true">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                </span>
              </div>
              <div class="mt-8">
                <h3 class="text-lg font-medium">
                  <a href="#" class="focus:outline-none">
                    <!-- Extend touch target to entire panel -->
                    <span class="absolute inset-0" aria-hidden="true"></span>
                    Support
                  </a>
                </h3>
                <p class="mt-2 text-sm text-gray-500">
                  Have a question about sales or something else? Open a support
                  ticket to gain assistance for your inquiry.
                </p>
              </div>
              <span class="pointer-events-none absolute top-6 right-6 text-gray-300 group-hover:text-gray-400"
                aria-hidden="true">
                <svg class="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 24 24">
                  <path
                    d="M20 4h1a1 1 0 00-1-1v1zm-1 12a1 1 0 102 0h-2zM8 3a1 1 0 000 2V3zM3.293 19.293a1 1 0 101.414 1.414l-1.414-1.414zM19 4v12h2V4h-2zm1-1H8v2h12V3zm-.707.293l-16 16 1.414 1.414 16-16-1.414-1.414z" />
                </svg>
              </span>
            </router-link>

            <router-link :to="{ name: 'StringerKnowledgeBase' }"
              class="block rounded-bl-lg rounded-br-lg sm:rounded-bl-none relative group bg-white p-6 focus-within:ring-2 focus-within:ring-inset focus-within:ring-blue-500">
              <div>
                <span class="rounded-lg inline-flex p-3 bg-indigo-50 text-indigo-700 ring-4 ring-white">
                  <!-- Heroicon name: outline/users -->
                  <svg class="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                    stroke="currentColor" aria-hidden="true">
                    <path fill="#fff" d="M12 14l9-5-9-5-9 5 9 5z" />
                    <path fill="#fff"
                      d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14zm-4 6v-7.5l4-2.222" />
                  </svg>
                </span>
              </div>
              <div class="mt-8">
                <h3 class="text-lg font-medium">
                  <a href="#" class="focus:outline-none">
                    <!-- Extend touch target to entire panel -->
                    <span class="absolute inset-0" aria-hidden="true"></span>
                    Knowledge Base
                  </a>
                </h3>
                <p class="mt-2 text-sm text-gray-500">
                  Check out a vast knowledge base containing publications of the
                  LSM Stringer Handbook and general FAQ's.
                </p>
              </div>
              <span class="pointer-events-none absolute top-6 right-6 text-gray-300 group-hover:text-gray-400"
                aria-hidden="true">
                <svg class="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 24 24">
                  <path
                    d="M20 4h1a1 1 0 00-1-1v1zm-1 12a1 1 0 102 0h-2zM8 3a1 1 0 000 2V3zM3.293 19.293a1 1 0 101.414 1.414l-1.414-1.414zM19 4v12h2V4h-2zm1-1H8v2h12V3zm-.707.293l-16 16 1.414 1.414 16-16-1.414-1.414z" />
                </svg>
              </span>
            </router-link>
          </div>
        </section>
      </div>

      <!-- Right column -->
      <div class="grid grid-cols-1 gap-4">
        <!-- <section aria-labelledby="account-checklist">
          <div class="rounded-lg bg-red-600 overflow-hidden shadow text-white">
            <div class="p-6">
              <h2 class="text-base font-medium" id="account-checklist">
                Account Checkup
              </h2>
            </div>
          </div>
        </section> -->

        <section aria-labelledby="stats-title">
          <div class="rounded-lg bg-white overflow-hidden shadow">
            <div class="p-6">
              <div class="flex flex-row items-center">
                <h2 class="text-base font-medium text-gray-900 flex-1" id="stats-title">
                  Your Statistics
                </h2>
                <span class="text-xs font-light text-gray-500" v-tippy title="Stats are updated once every six hours."
                  v-bind:key="renderTs + 'renderTs'">updated {{ userInfo.stats.lastUpdated | timeAgo }}</span>
              </div>
              <div class="flow-root text-sm mt-6">
                <div class="-my-5 divide-y divide-gray-200 pb-2">
                  <div class="flex flex-row items-center py-3 group">
                    <div class="flex-1">
                      Videos to sales ratio
                      <i class="fad fa-info-circle text-gray-600 opacity-0 group-hover:opacity-100 transition-all"
                        v-tippy title="Derived from all of videos and sales over the past year."></i>
                    </div>
                    <span>{{ userInfo.stats.videosToSalesRatio }}</span>
                  </div>
                  <div class="flex flex-row items-center py-3 group">
                    <div class="flex-1">
                      Videos past year
                      <i class="fad fa-info-circle text-gray-600 opacity-0 group-hover:opacity-100 transition-all"
                        v-tippy
                        title="Calculated by counting the videos uploaded last year (365 days) and the year prior. The percentage is found by finding the percent increase from the year prior to the most recent year."></i>
                    </div>
                    <span>{{ userInfo.stats.videosPastYear }}
                      <span class="text-gray-500 text-xs" :class="{
                        'text-red-600':
                          userInfo.stats.videosPercentageIncrease < 0,
                        'text-green-600':
                          userInfo.stats.videosPercentageIncrease > 0
                      }">(<span v-if="userInfo.stats.videosPercentageIncrease > 0">+</span>{{
  Math.round(userInfo.stats.videosPercentageIncrease)
}}%)</span></span>
                  </div>
                  <div class="flex flex-row items-center py-3 group">
                    <div class="flex-1">
                      Sales past year
                      <i class="fad fa-info-circle text-gray-600 opacity-0 group-hover:opacity-100 transition-all"
                        v-tippy
                        title="Calculated by counting the sales in the last year (365 days) and the year prior. The percentage is found by finding the percent increase from the year prior to the most recent year."></i>
                    </div>
                    <span>{{ userInfo.stats.salesPastYear }}
                      <span class="text-gray-500 text-xs" :class="{
                        'text-red-600':
                          userInfo.stats.salesPercentageIncrease < 0,
                        'text-green-600':
                          userInfo.stats.salesPercentageIncrease > 0
                      }">(<span v-if="userInfo.stats.salesPercentageIncrease > 0">+</span>{{
  Math.round(userInfo.stats.salesPercentageIncrease)
}}%)</span></span>
                  </div>
                  <div class="flex flex-row items-center py-3 group">
                    <div class="flex-1">
                      Lifetime videos
                      <i class="fad fa-info-circle text-gray-600 opacity-0 group-hover:opacity-100 transition-all"
                        v-tippy
                        title="A count of all of the videos you have ever uploaded to Live Storms Media's CDN."></i>
                    </div>
                    <span>{{ userInfo.stats.videosTotal }} </span>
                  </div>
                  <div class="flex flex-row items-center py-3 group">
                    <div class="flex-1">
                      Lifetime sales
                      <i class="fad fa-info-circle text-gray-600 opacity-0 group-hover:opacity-100 transition-all"
                        v-tippy
                        title="A count of all of the sales you have ever had via the Live Storms Media's CDN. This value does not account for sales prior to January 1, 2017 or stock sales."></i>
                    </div>
                    <span>{{ userInfo.stats.salesTotal }} </span>
                  </div>
                  <div class="flex flex-row items-center py-3 group">
                    <div class="flex-1">
                      Average video rating
                      <i class="fad fa-info-circle text-gray-600 opacity-0 group-hover:opacity-100 transition-all"
                        v-tippy title="An average of all video ratings within the past 365 days."></i>
                    </div>
                    <span>
                      <StarRating :rating="Math.round(userInfo.stats.averageVideoRating)" />
                    </span>
                  </div>
                  <div class="flex flex-row items-center py-3 group">
                    <div class="flex-1">
                      Lifetime Social Earnings
                      <i class="fad fa-info-circle text-gray-600 opacity-0 group-hover:opacity-100 transition-all"
                        v-tippy
                        title="The amount of money that you have made through our social media platforms' ad revenue since September 2022."></i>
                    </div>
                    <span>
                      {{ userInfo.stats.socialIncomeLifetime | displayMoney }}
                    </span>
                  </div>
                  <div class="flex flex-row items-center py-3 group">
                    <div class="flex-1">
                      Social Earnings Balance
                      <i class="fad fa-info-circle text-gray-600 opacity-0 group-hover:opacity-100 transition-all"
                        v-tippy title="Payouts will occur once your account reaches a $100 threshold."></i>
                    </div>
                    <span>
                      {{ userInfo.stats.socialIncomeBalance | displayMoney }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- Announcements -->
        <section aria-labelledby="announcements-title">
          <div class="rounded-lg bg-white overflow-hidden shadow">
            <div class="p-6">
              <h2 class="text-base font-medium text-gray-900" id="announcements-title">
                Recent Announcements
              </h2>
              <div class="flow-root mt-6">
                <ul class="-my-5 divide-y divide-gray-200">
                  <li class="py-5" v-for="announcement in announcements" v-bind:key="announcement.id">
                    <router-link tag="div"
                      class="group relative focus-within:ring-2 focus-within:ring-blue-500 cursor-pointer" :to="{
                        name: 'StringerAnnouncementView',
                        params: { uuid: announcement.id }
                      }">
                      <div class="text-sm font-semibold text-gray-800 flex">
                        <span class="group-hover:underline focus:outline-none flex-1">
                          {{ announcement.title }}
                        </span>
                        <div class="space-x-2">
                          <p
                            class="px-2 inline-flex items-center text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800 space-x-1.5">
                            <i class="fas fa-comment text-gray-400"></i>
                            <span>{{ announcement.commentsCount }}</span>
                          </p>
                          <p
                            class="px-2 inline-flex  items-center text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800 space-x-1.5">
                            <i class="fas fa-user text-gray-400"></i>
                            <span>{{ announcement.viewsCount }}</span>
                          </p>
                        </div>
                      </div>
                      <p class="mt-1 text-sm text-gray-600 line-clamp-3">
                        {{ announcement.message }}
                      </p>
                    </router-link>
                  </li>
                </ul>
              </div>
              <div class="mt-6">
                <router-link to="/stringer/announcements"
                  class="w-full flex justify-center items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
                  View all
                </router-link>
              </div>
            </div>
          </div>
        </section>

        <section aria-labelledby="recent-hires-title">
          <div class="rounded-lg bg-white overflow-hidden shadow">
            <div class="p-6">
              <h2 class="text-base font-medium text-gray-900" id="recent-hires-title">
                Available Apps
              </h2>
              <div class="flow-root mt-6">
                <ul class="-my-5 divide-y divide-gray-200">
                  <li class="py-4">
                    <div class="flex items-center space-x-4">
                      <div class="flex-shrink-0">
                        <img class="rounded h-8 w-8 mr-2" src="../../assets/lsm-app-icon.png" />
                      </div>
                      <div class="flex-1 min-w-0">
                        <p class="text-sm font-medium text-gray-900 truncate">
                          LSM2GO
                        </p>
                        <p class="text-sm text-gray-500 truncate">
                          Upload videos, view sales, and stay up to date and
                          discuss with others via announcements, all within the
                          LSM2GO mobile app. The mobile app has recently been
                          updated to include new features and to overall improve
                          performance.
                        </p>
                      </div>
                      <div class="space-x-2">
                        <a href="https://apps.apple.com/us/app/lsm2go/id1394983358" target="_blank"
                          class="inline-flex items-center shadow-sm px-2.5 py-1 border border-gray-300 text-sm leading-5 font-medium rounded-full text-gray-700 bg-white hover:bg-gray-50">
                          <i class="fab fa-apple"></i>
                        </a>
                        <a href="https://play.google.com/store/apps/details?id=tech.cirrus.lsm2go&hl=en_US"
                          target="_blank"
                          class="inline-flex items-center shadow-sm px-2.5 py-1 border border-gray-300 text-sm leading-5 font-medium rounded-full text-gray-700 bg-white hover:bg-gray-50">
                          <i class="fab fa-android"></i>
                        </a>
                      </div>
                    </div>
                  </li>

                  <li class="py-4">
                    <div class="flex items-center space-x-4">
                      <div class="flex-shrink-0">
                        <img class="rounded h-8 w-8 mr-2" src="../../assets/livechasing-app-icon.png" />
                      </div>
                      <div class="flex-1 min-w-0">
                        <p class="text-sm font-medium text-gray-900 truncate">
                          Live Chasing
                        </p>
                        <p class="text-sm text-gray-500 truncate">
                          Track storm chasers live accross the United States and
                          beyond!
                        </p>
                      </div>
                      <div class="space-x-2">
                        <a href="https://apps.apple.com/us/app/live-chasing/id1124296614" target="_blank"
                          class="inline-flex items-center shadow-sm px-2.5 py-1 border border-gray-300 text-sm leading-5 font-medium rounded-full text-gray-700 bg-white hover:bg-gray-50">
                          <i class="fab fa-apple"></i>
                        </a>
                        <a href="https://play.google.com/store/apps/details?id=tv.livewx.livechasing&hl=en_US&gl=US"
                          target="_blank"
                          class="inline-flex items-center shadow-sm px-2.5 py-1 border border-gray-300 text-sm leading-5 font-medium rounded-full text-gray-700 bg-white hover:bg-gray-50">
                          <i class="fab fa-android"></i>
                        </a>
                      </div>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>
<script>
import * as Config from "@/config";
import { mapGetters } from "vuex";

import moment from "moment"
import VueApexCharts from "vue-apexcharts";
import AccountIcon from "@/components/AccountIcon";
import StarRating from "@/components/general/StarRating";

export default {
  name: "StringerLayout",
  data() {
    return {
      renderTs: null,
      renderTsInterval: null,
      announcements: [],
      series: [
        {
          name: "Uploads",
          data: [],
        },
        {
          name: "Video Sales",
          data: []
        },
        {
          name: "Stream Sales",
          data: []
        }
      ],
      chartOptions: {
        chart: {
          type: "area",
          toolbar: {
            show: false
          },
          selection: {
            enabled: false
          },
          sparkline: {
            enabled: true
          },
          zoom: {
            enabled: false
          }
        },
        grid: {
          show: false,
          padding: {
            top: 4,
            left: 0,
            right: 0,
            bottom: 0
          }
        },
        stroke: {
          curve: "smooth"
        },
        tooltip: {
          x: {
            show: true
          },
          y: {
            formatter: function (value) {
              return Math.round(value);
            }
          }
        }
      }
    };
  },
  components: { AccountIcon, StarRating, VueApexCharts },
  computed: {
    ...mapGetters(["userInfo", "userInitials"])
  },
  mounted() {
    this.getAnnouncements();
    this.getSalesOverviewGraphData()
    this.renderTsInterval = setInterval(
      function () {
        console.log("up");
        this.renderTs = Date.now();
      }.bind(this),
      60000
    );
  },
  destroyed() {
    if (this.renderTsInterval !== null)
      clearInterval(this.renderTsInterval) && this.renderTsInterval == true;
  },
  methods: {
    getAnnouncements() {
      this.$http
        .get(Config.apiPath + "/api/v2/stringer/announcements?count=3")
        .then(response => {
          this.$set(this, "announcements", response.data);
        });
    },
    getSalesOverviewGraphData() {
      this.$http
        .get(Config.apiPath + "/api/v2/stringer/graphs/sales-overview")
        .then(response => {
          this.$set(this, "salesOverview", response.data);

          let uploads = [];
          for (const [key, value] of Object.entries(response.data.uploads)) {
            uploads.push({
              x: moment(key, "YYYY-MM").format("MMMM, YYYY"),
              y: value
            });
          }
          this.series[0].data = uploads;

          let videoSales = [];
          for (const [key, value] of Object.entries(response.data.video)) {
            videoSales.push({
              x: moment(key, "YYYY-MM").format("MMMM, YYYY"),
              y: value
            });
          }
          this.series[1].data = videoSales;

          let streamSales = [];
          for (const [key, value] of Object.entries(response.data.stream)) {
            streamSales.push({
              x: moment(key, "YYYY-MM").format("MMMM, YYYY"),
              y: value
            });
          }
          this.series[2].data = streamSales;

          this.createSalesGraph();
        })
    },
    createSalesGraph() {
      this.$refs.overviewChart.updateSeries(
        [
          {
            data: this.series[0].data
          },
          {
            data: this.series[1].data
          },
          {
            data: this.series[2].data
          }
        ],
        false,
        true
      );
    }
  },
  filters: {
    displayMoney: function (value) {
      var formatter = new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD"
      });
      return formatter.format(value);
    },
  }
};
</script>
